import React from 'react';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';

import {
    Carousel,
    CarouselHeader,
    CarouselTitle,
    CarouselLink,
    CarouselBody,
    CarouselSlider,
    CarouselSliderItem,
    CarouselPagination,
} from '@lux/carousel';
import { cn, LightModeProvider } from '@lux/core';
import { Text, Preset, FunctionColor } from '@lux/text';

import { Translate, TranslateScope } from '@pegasus/translation';
import { getCampaignMobileImage } from '@browsing/campaign';
import { useVisibilitySessionReplay } from '@browsing/hooks';
import { trackSelectContent } from '@browsing/tracking';
import { ErrorBoundary } from '@browsing/components';

import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { Context, EcommerceCreativeName, EcommerceElement } from '@pegasus/tracker';
import { Features } from '@browsing/config';
import { useTrackCampaignInView } from '../../hooks';

import { CampaignsCarouselCard } from './campaignsCarouselCard';
import { ActiveFeature } from '../../campaigns/grid/context/useGridLayoutContextValue';
import { useCampaignsCarouselData } from '../hooks/useCampaignsCarouselData';

interface CampaignsCarouselProps {
    activeFeature: ActiveFeature;
}

const CampaignsCarouselInternal: React.FC<CampaignsCarouselProps> = ({ activeFeature }) => {
    const replayItemId = `campaigns-carousel-${activeFeature}`;
    const { ref: rootRef, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false,
    });
    const categoryTabId = useSelector(getCategoryTabId);
    const carouselData = useCampaignsCarouselData();

    const carouselDataEntry = carouselData?.[activeFeature];

    const { campaigns, translationsPath, carouselHref, trackingParams, emphasisLevel } =
        carouselDataEntry || {
            campaigns: [],
            translationsPath: '',
            carouselHref: '',
            trackingParams: {
                element: '' as EcommerceElement,
                creativeName: '' as EcommerceCreativeName,
                itemListName: '' as Context,
            },
            emphasisLevel: undefined,
        };

    const handleTrackCampaignInView = useTrackCampaignInView({
        campaigns,
        categoryTabId,
        trackingParams: {
            element: trackingParams.element,
            creativeName: trackingParams.creativeName,
            itemListName: trackingParams.itemListName,
        },
    });

    useVisibilitySessionReplay({
        itemId: replayItemId,
        isEnabled: activeFeature === 'leavingSoon',
        inView,
        inViewRef: rootRef,
        replayFeature: Features.LeavingSoon,
    });

    const handleScroll = () => {
        trackSelectContent(trackingParams.element, 'swipe');
    };

    const handleSeeAllClick = () => {
        trackSelectContent(trackingParams.element, 'click', {
            contentId: 'see_all',
        });
    };

    if (!carouselData || !activeFeature || !carouselData[activeFeature]) {
        return null;
    }

    return (
        <TranslateScope path={translationsPath}>
            <Carousel
                href={carouselHref}
                emphasisLevel={emphasisLevel}
                colorMode="default"
                id={replayItemId}
            >
                <CarouselHeader>
                    <div
                        className={cn(
                            'w-fit',
                            'px-xs py-xxs',
                            activeFeature === 'newDeals'
                                ? 'bg-complementary-neon-fresh'
                                : 'bg-[#9c1292]',
                        )}
                    >
                        <LightModeProvider>
                            <Text
                                preset={Preset.S}
                                bold
                                color={
                                    activeFeature === 'leavingSoon'
                                        ? FunctionColor.function0
                                        : FunctionColor.function100
                                }
                            >
                                <Translate message="label" />
                            </Text>
                        </LightModeProvider>
                    </div>
                    <CarouselTitle>
                        <Translate message="carouselTitle" />
                    </CarouselTitle>
                    <CarouselLink onClick={handleSeeAllClick}>
                        <Translate message="seeAll" />
                    </CarouselLink>
                </CarouselHeader>
                <CarouselBody
                    visibleItems={2.2}
                    visibleItemsXs={3.5}
                    visibleItemsMd={5.3}
                    visibleItemsLg={5.6}
                    onScroll={handleScroll}
                    withOverflow
                    data-testid="responsive-slider"
                >
                    <CarouselSlider>
                        {campaigns.map((campaign, index) => {
                            const media = getCampaignMobileImage(campaign);
                            return (
                                <CarouselSliderItem key={campaign.campaignId}>
                                    <TranslateScope path="myLounge">
                                        <CampaignsCarouselCard
                                            campaign={campaign}
                                            media={media}
                                            index={index}
                                            onInView={handleTrackCampaignInView}
                                            trackingParams={trackingParams}
                                        />
                                    </TranslateScope>
                                </CarouselSliderItem>
                            );
                        })}
                    </CarouselSlider>
                    <CarouselPagination />
                </CarouselBody>
            </Carousel>
        </TranslateScope>
    );
};

export const CampaignsCarousel: React.FC<CampaignsCarouselProps> = props => (
    <ErrorBoundary componentName="CampaignsCarousel">
        <CampaignsCarouselInternal {...props} />
    </ErrorBoundary>
);
